import axios from 'axios';
import queryString from 'query-string';
import {getToken, isAuthenticated} from './SessionController';

export const getAPIBaseURL = () => localStorage.getItem("apiBaseURL") || process.env.VUE_APP_API_ADDRESS;
export const getAPIAuthProvider = () => localStorage.getItem("apiAuthProvider") || process.env.VUE_APP_AUTH_PROVIDER;

const trimmedBaseURL = (getAPIBaseURL() || '').replace(
  /\/$/,
  '',
);
const authProvider = getAPIAuthProvider();

const sendRequest = async (endpoint, type, data = {}) => {
  // Inject access token if available
  const config = { headers: {} };
  if (isAuthenticated()) {
    config.headers['x-access-token'] = getToken();
  }

  let promise;
  switch (type) {
    case 'POST':
      promise = axios.post(trimmedBaseURL + endpoint, data, config);
      break;
    case 'GET': {
      let qs = queryString.stringify(data);
      promise = axios.get(
        trimmedBaseURL + endpoint + (qs ? `?${qs}` : ''),
        config,
      );
      break;
    }
    case 'GET-FILE': {
      let qs = queryString.stringify(data);
      promise = axios.get(trimmedBaseURL + endpoint + (qs ? `?${qs}` : ''), {
        ...config,
        responseType: 'blob',
      });
      break;
    }
    case 'PUT':
      promise = axios.put(trimmedBaseURL + endpoint, data, config);
      break;
    default:
      throw new Error(`Unknown request type "${type}"`);
  }

  try {
    const result = await promise;

    return {
      success: true,
      status: result.status,
      data: result.data.message || result.data,
    };
  } catch (e) {
    if (e.response) {
      const responseData = e.response.data;

      // We don't want an infinite recursive loop, so we'll only logout the user if they aren't already trying to do so
      if (
        e.response.status === 401 &&
        isAuthenticated() &&
        !endpoint.includes('logout')
      ) {
        await logout();
      }

      return {
        success: false,
        status: e.response.status,
        data: `Error ${e.response.status || 500}: ${
          responseData.message || responseData || 'Unknown Error Occurred'
        }${responseData.error ? ' - ' + responseData.error : ''}`,
      };
    }

    return {
      success: false,
      status: 400,
      data: 'Error 400: A network error occurred',
    };
  }
};

export const getLoginRedirectURL = async (redirectTo) =>
  sendRequest(`/auth/${authProvider}/login`, 'POST', {
    callbackURL: `${location.origin}/callback`,
    redirectTo: redirectTo,
  });
export const authCallback = async (state, code) =>
  sendRequest(`/auth/${authProvider}/callback`, 'POST', {
    state: state,
    code: code,
  });
export const refreshToken = async (refreshToken) =>
  sendRequest(`/auth/${authProvider}/refresh`, 'POST', {
    refreshToken: refreshToken,
  });
export const logout = async (refreshToken) =>
  sendRequest(`/auth/${authProvider}/logout`, 'POST', {
    refreshToken: refreshToken,
  });


export const syncMailingLists = async (force) =>
  sendRequest(`/api/action/syncMailingLists`, 'POST', {
    forceUpdate: !!force
  });
export const verifyMailingList = async () =>
  sendRequest(`/api/action/verifyMailingList`, 'POST');
export const templateTest = async () =>
  sendRequest(`/api/action/templateTest`, 'POST');

export const getStatistics = async (update) =>
  sendRequest('/api/action/getStatistics', 'GET', {
    update: update ? 'true' : 'false',
  });
export const getObject = async (objectType, query) =>
  sendRequest(`/api/get/${objectType}`, 'POST', query || {});
export const createObject = async (objectType, body) =>
  sendRequest(`/api/create/${objectType}`, 'POST', body || {});
export const deleteObject = async (objectType, query) =>
  sendRequest(`/api/delete/${objectType}`, 'POST', query || {});
export const editObject = async (objectType, filter, changes, noFlatten = false) =>
  sendRequest(`/api/edit/${objectType}`, 'POST', {
    filter: filter,
    changes: changes,
    noFlatten: noFlatten
  });

export const getFileURL = async (filename) =>
  sendRequest(`/api/gridfs`, 'GET-FILE', {
    filename: filename,
    bucket: "resumes"
  });

export const gradeCandidate = async (candidateID, grades) => sendRequest('/api/action/gradeCandidate', 'POST', {
  candidateID: candidateID,
  grade: grades
});

export const getCandidate = async (category) => sendRequest(`/api/action/getCandidate${ category ? '?category=' + category : '' }`, 'GET');

export const getRanks = async () =>
  sendRequest(`/api/action/getRanks`, 'GET');

export const releaseApplicationStatus = async () => sendRequest(`/api/action/releaseApplicationStatus`, 'POST');
export const assignApplicationStatus = async (legit, waitlistOver, waitlistDeadline) => sendRequest(`/api/action/assignApplicationStatus`, 'POST', {
  legit: legit,
  waitlistOver: waitlistOver,
  waitlistDeadline: waitlistDeadline
});

export const resumeExport = async () => sendRequest(`/api/action/resumeExport`, 'GET-FILE');

export const createAPIToken = async(groups, description) => sendRequest('/api/action/createAPIToken', 'POST', {
  groups,
  description
});

export const getDiscordMetadata = async(userID) => sendRequest('/api/action/discordMetadata', 'GET', {
  userID
});

export const disassociateDiscord = async(userID) => sendRequest('/api/action/disassociateDiscord', 'POST', {
  userID
});

export const getDiscordOAuthUrl = async (redirectUrl) => sendRequest('/api/action/discordOAuthUrl', 'POST', {
  redirectUrl
});

export const associateDiscord = async (state, code) => sendRequest('/api/action/associateDiscord', 'POST', {
  state, code
});

// userList is an array of {userID: string, userType: "User" | "ExternalUser"}
export const multiCheckInQR = async (userList) => sendRequest('/api/action/multiCheckInQR', 'POST', {
  userList
});

export const checkIn = async (userID, userType) => sendRequest('/api/action/checkIn', 'POST', {
  userID, userType
});